import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ZonaDeImagen, motivoDeRechazo } from "@/components/informes/ZonaDeImagen";
import { useQuitarImagenDelInforme, useSubirImagenDelInforme } from "@/hooks/useInformes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { urlDeLaImagenDelInforme, type ImagenDeInforme } from "@/lib/informes";

/**
 * La imagen satelital ÚNICA de un informe QUE YA EXISTE: cada archivo soltado
 * se sube al momento.
 *
 * Es la mitad conectada de {@see ZonaDeImagen}. La otra —el alta, donde el
 * informe todavía no tiene id— es {@see ImagenPendiente}: ahí no hay a dónde
 * subir, así que el archivo se retiene y viaja después de crear el informe.
 *
 * REVERSIÓN de la versión "una imagen por bloque de roles": esa agrupación
 * nació de la 22160, que en realidad eran cinco tasaciones distintas metidas
 * en un documento. Con un solo predio por informe (el caso corriente), una
 * imagen alcanza; el caso de roles en comunas distintas se explica con la
 * nota de la imagen, no con una segunda captura.
 */
export function ImagenDeLaPropiedad({
  informeId,
  imagen,
}: {
  informeId: number;
  imagen: ImagenDeInforme | null;
}) {
  const subir = useSubirImagenDelInforme(informeId);
  const quitar = useQuitarImagenDelInforme(informeId);
  const [error, setError] = useState<string | null>(null);

  const ocupado = subir.isPending || quitar.isPending;

  return (
    <ZonaDeImagen
      src={imagen ? urlDeLaImagenDelInforme(informeId, imagen.hash) : null}
      peso={imagen?.bytes ?? null}
      ocupado={ocupado}
      error={error}
      onArchivo={(archivo) => {
        const rechazo = motivoDeRechazo(archivo);
        if (rechazo) {
          setError(rechazo);
          return;
        }

        setError(null);
        subir.mutate(archivo, {
          onSuccess: () => toast.success("Imagen guardada."),
          onError: (e) => {
            // El 422 del servidor manda sobre el chequeo previo: rechaza por
            // cosas que el navegador no mira, como que los bytes no sean los
            // que el tipo declarado dice.
            const errores = parseValidationErrors(e);
            setError(
              errores?.imagen ??
                getApiErrorMessage(e, "No pudimos guardar la imagen. Vuelve a intentarlo."),
            );
          },
        });
      }}
      onQuitar={() =>
        quitar.mutate(undefined, {
          onSuccess: () => {
            setError(null);
            toast.success("Imagen quitada.");
          },
          onError: (e) => setError(getApiErrorMessage(e, "No pudimos quitar la imagen.")),
        })
      }
    />
  );
}

/**
 * La imagen mientras el informe TODAVÍA NO EXISTE (formulario de creación).
 *
 * No sube nada: el endpoint es `POST /informes/{informe}/imagen` y ese id lo
 * asigna recién el alta. El archivo se retiene en el estado del padre —que es
 * quien lo va a necesitar cuando tenga el id— y se muestra desde la memoria
 * del navegador con `createObjectURL`.
 *
 * El archivo vive ARRIBA y no acá adentro a propósito: si lo guardara este
 * componente, la pantalla que crea el informe tendría que ir a buscárselo con
 * un `ref` para poder subirlo. Quien necesita el dato es quien lo tiene.
 */
export function ImagenPendiente({
  archivo,
  onArchivo,
}: {
  archivo: File | null;
  onArchivo: (archivo: File | null) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);

  // `createObjectURL` reserva memoria del navegador hasta que se la revoca a
  // mano: sin el `return` de limpieza, elegir varias imágenes seguidas deja
  // las anteriores retenidas hasta que se recarga la página.
  useEffect(() => {
    if (!archivo) {
      setVistaPrevia(null);
      return;
    }

    const url = URL.createObjectURL(archivo);
    setVistaPrevia(url);

    return () => URL.revokeObjectURL(url);
  }, [archivo]);

  return (
    <ZonaDeImagen
      src={vistaPrevia}
      peso={archivo?.size ?? null}
      error={error}
      onArchivo={(elegido) => {
        const rechazo = motivoDeRechazo(elegido);
        if (rechazo) {
          setError(rechazo);
          return;
        }

        setError(null);
        onArchivo(elegido);
      }}
      onQuitar={() => {
        setError(null);
        onArchivo(null);
      }}
    />
  );
}
